import React, { useState, useEffect } from "react";
import { api, formatErr } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Truck, Plus, MapPin, Phone, User, Package, CheckCircle2, XCircle, RotateCcw, Clock, MessageCircle, RefreshCw, Trash2 } from "lucide-react";

const STATUS_META = {
  pending: { label: "بانتظار التعيين", color: "bg-slate-100 text-slate-700", icon: Clock },
  assigned: { label: "تم التعيين", color: "bg-blue-100 text-blue-700", icon: User },
  in_route: { label: "في الطريق", color: "bg-amber-100 text-amber-700", icon: Truck },
  delivered: { label: "تم التسليم", color: "bg-emerald-100 text-emerald-700", icon: CheckCircle2 },
  returned: { label: "مرتجع", color: "bg-red-100 text-red-700", icon: RotateCcw },
  cancelled: { label: "ملغى", color: "bg-slate-100 text-slate-500", icon: XCircle },
};

const NEXT_STATUS = {
  pending: ["assigned", "cancelled"],
  assigned: ["in_route", "cancelled"],
  in_route: ["delivered", "returned"],
  delivered: [],
  returned: [],
  cancelled: [],
};

export default function Deliveries() {
  const [items, setItems] = useState([]);
  const [drivers, setDrivers] = useState([]);
  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dlg, setDlg] = useState(false);
  const [form, setForm] = useState({ invoice_id: "", driver_id: "", address: "", phone: "", notes: "", delivery_fee: 0 });
  const [statusFilter, setStatusFilter] = useState("all");

  const load = async () => {
    setLoading(true);
    try {
      const [d, dr, invs] = await Promise.all([
        api.get(`/deliveries${statusFilter !== "all" ? `?status=${statusFilter}` : ""}`),
        api.get("/drivers"),
        api.get("/invoices?kind=sale"),
      ]);
      setItems(d.data);
      setDrivers(dr.data);
      // Filter to invoices without an active delivery
      setInvoices(invs.data.slice(0, 200));
    } catch (e) { toast.error(formatErr(e)); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, [statusFilter]);

  const create = async () => {
    if (!form.invoice_id) return toast.error("اختر فاتورة");
    try {
      const driver = drivers.find(d => d.id === form.driver_id);
      await api.post("/deliveries", {
        invoice_id: form.invoice_id,
        driver_id: form.driver_id || null,
        driver_name: driver?.name || "",
        address: form.address, phone: form.phone,
        notes: form.notes, delivery_fee: +form.delivery_fee || 0,
      });
      toast.success("تم إنشاء رحلة التوصيل");
      setDlg(false); setForm({ invoice_id: "", driver_id: "", address: "", phone: "", notes: "", delivery_fee: 0 });
      load();
    } catch (e) { toast.error(formatErr(e)); }
  };

  const updateStatus = async (d, newStatus) => {
    const notes = newStatus === "returned" ? window.prompt("سبب الإرجاع (اختياري):") : "";
    try {
      await api.post(`/deliveries/${d.id}/status`, { status: newStatus, notes: notes || "" });
      toast.success(`الحالة: ${STATUS_META[newStatus]?.label}`);
      load();
    } catch (e) { toast.error(formatErr(e)); }
  };

  const assignDriver = async (d) => {
    if (drivers.length === 0) return toast.error("لا يوجد سائقون - أضف مستخدماً بدور أمين مستودع أو is_driver=true");
    const options = drivers.map((dr, i) => `${i + 1}. ${dr.name}`).join("\n");
    const idx = window.prompt(`اختر رقم السائق:\n${options}`);
    if (!idx) return;
    const dr = drivers[parseInt(idx) - 1];
    if (!dr) return toast.error("رقم غير صحيح");
    try {
      await api.post(`/deliveries/${d.id}/status`, { status: "assigned", driver_id: dr.id, driver_name: dr.name });
      toast.success(`تم التعيين للسائق ${dr.name}`);
      load();
    } catch (e) { toast.error(formatErr(e)); }
  };

  const openWhatsApp = async (d) => {
    try {
      const { data } = await api.get(`/deliveries/${d.id}/whatsapp-text`);
      if (!data.wa_url) { toast.error("لا يوجد رقم هاتف مسجّل للعميل"); return; }
      window.open(data.wa_url, "_blank", "noopener");
    } catch (e) { toast.error(formatErr(e)); }
  };

  const del = async (d) => {
    if (!window.confirm("حذف رحلة التوصيل؟")) return;
    try { await api.delete(`/deliveries/${d.id}`); toast.success("تم الحذف"); load(); }
    catch (e) { toast.error(formatErr(e)); }
  };

  const grouped = items.reduce((acc, d) => {
    (acc[d.status] = acc[d.status] || []).push(d);
    return acc;
  }, {});

  const kanbanCols = ["pending", "assigned", "in_route", "delivered"];

  return (
    <div className="space-y-4" data-testid="deliveries-page">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div>
          <h1 className="font-heading font-bold text-2xl flex items-center gap-2"><Truck className="w-6 h-6 text-primary" /> لوحة التوصيل</h1>
          <p className="text-sm text-muted-foreground mt-1">تتبع طلبات التوصيل، تعيين السائقين، وإرسال تحديثات الحالة عبر واتساب</p>
        </div>
        <div className="flex gap-2">
          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger className="w-40 h-9"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">الكل</SelectItem>
              {Object.entries(STATUS_META).map(([k, v]) => <SelectItem key={k} value={k}>{v.label}</SelectItem>)}
            </SelectContent>
          </Select>
          <Button variant="outline" size="sm" onClick={load}><RefreshCw className="w-4 h-4 me-1" /> تحديث</Button>
          <Button onClick={() => setDlg(true)} className="gap-2" data-testid="new-delivery-btn"><Plus className="w-4 h-4" /> رحلة جديدة</Button>
        </div>
      </div>

      {loading ? <div className="text-center text-muted-foreground py-12">جاري التحميل...</div> : (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-3">
          {kanbanCols.map(status => {
            const meta = STATUS_META[status];
            const list = grouped[status] || [];
            const Icon = meta.icon;
            return (
              <div key={status} className={`border-2 border-dashed rounded-lg p-2 min-h-[300px] ${status === "in_route" ? "border-amber-300" : "border-border"}`} data-testid={`kanban-${status}`}>
                <div className={`flex items-center gap-2 px-2 py-1.5 rounded font-bold text-sm mb-2 ${meta.color}`}>
                  <Icon className="w-4 h-4" /> {meta.label}
                  <span className="me-auto text-xs font-mono">{list.length}</span>
                </div>
                <div className="space-y-2">
                  {list.map(d => (
                    <div key={d.id} className="p-3 rounded-lg border bg-card hover:border-primary/40 transition" data-testid={`delivery-${d.id}`}>
                      <div className="flex items-start justify-between mb-1">
                        <div className="font-mono text-xs text-muted-foreground">#{d.invoice_number}</div>
                        <Button size="icon" variant="ghost" className="h-6 w-6 text-destructive" onClick={() => del(d)}><Trash2 className="w-3 h-3" /></Button>
                      </div>
                      <div className="font-medium text-sm">{d.customer_name}</div>
                      <div className="text-xs text-muted-foreground font-mono mt-0.5">{(d.invoice_total || 0).toLocaleString("ar-EG")} {d.invoice_currency}</div>
                      {d.address && <div className="text-xs mt-1 flex items-start gap-1"><MapPin className="w-3 h-3 mt-0.5 flex-shrink-0 text-muted-foreground" /><span className="line-clamp-2">{d.address}</span></div>}
                      {d.phone && <div className="text-xs mt-1 flex items-center gap-1"><Phone className="w-3 h-3 text-muted-foreground" /><span className="font-mono">{d.phone}</span></div>}
                      {d.driver_name && <div className="text-xs mt-1 flex items-center gap-1 text-blue-700"><User className="w-3 h-3" /><span>{d.driver_name}</span></div>}
                      {d.notes && <div className="text-[10px] mt-1 bg-amber-50 p-1 rounded text-amber-800">{d.notes}</div>}
                      <div className="flex gap-1 mt-2 pt-2 border-t">
                        {status === "pending" && (
                          <Button size="sm" variant="outline" className="h-7 text-xs flex-1" onClick={() => assignDriver(d)}>عيّن سائق</Button>
                        )}
                        {NEXT_STATUS[status].map(next => (
                          <Button key={next} size="sm" variant="outline" className={`h-7 text-xs flex-1 ${next === "delivered" ? "text-emerald-700 border-emerald-200 hover:bg-emerald-50" : next === "returned" ? "text-red-700 border-red-200 hover:bg-red-50" : ""}`} onClick={() => updateStatus(d, next)} data-testid={`status-${d.id}-${next}`}>
                            {STATUS_META[next].label}
                          </Button>
                        ))}
                        {d.phone && (
                          <Button size="icon" variant="ghost" className="h-7 w-7 text-emerald-600" onClick={() => openWhatsApp(d)} title="إشعار واتساب" data-testid={`wa-${d.id}`}><MessageCircle className="w-3.5 h-3.5" /></Button>
                        )}
                      </div>
                    </div>
                  ))}
                  {list.length === 0 && (
                    <div className="text-center text-muted-foreground text-xs py-8">
                      {status === "pending" ? (
                        <button onClick={() => setDlg(true)} className="text-primary hover:underline flex items-center gap-1 mx-auto"><Plus className="w-3 h-3" /> أضف رحلة</button>
                      ) : "فارغ"}
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
       )}

      <Dialog open={dlg} onOpenChange={setDlg}>
        <DialogContent data-testid="new-delivery-dialog">
          <DialogHeader><DialogTitle>رحلة توصيل جديدة</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div>
              <Label>الفاتورة</Label>
              <select className="w-full h-10 px-2 border rounded bg-background" value={form.invoice_id} onChange={(e) => {
                const inv = invoices.find(x => x.id === e.target.value);
                setForm({ ...form, invoice_id: e.target.value, address: inv?.address || form.address, phone: inv?.phone || form.phone });
              }} data-testid="delivery-invoice">
                <option value="">— اختر فاتورة —</option>
                {invoices.map(inv => <option key={inv.id} value={inv.id}>{`#${inv.number} · ${inv.party_name} · ${(inv.total || 0).toLocaleString("ar-EG")}`}</option>)}
              </select>
            </div>
            <div>
              <Label>السائق (اختياري)</Label>
              <select className="w-full h-10 px-2 border rounded bg-background" value={form.driver_id} onChange={(e) => setForm({ ...form, driver_id: e.target.value })} data-testid="delivery-driver">
                <option value="">— بدون تعيين الآن —</option>
                {drivers.map(d => <option key={d.id} value={d.id}>{d.name}</option>)}
              </select>
              {drivers.length === 0 && <p className="text-xs text-amber-700 mt-1">لا يوجد سائقون — أضف مستخدماً بدور "أمين مستودع" أو فعّل is_driver=true</p>}
            </div>
            <div className="grid grid-cols-2 gap-2">
              <div><Label>هاتف العميل</Label><Input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} placeholder="مع رمز الدولة" /></div>
              <div><Label>رسوم التوصيل</Label><Input type="number" value={form.delivery_fee} onChange={(e) => setForm({ ...form, delivery_fee: +e.target.value || 0 })} /></div>
            </div>
            <div><Label>عنوان التوصيل</Label><Textarea rows={2} value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} /></div>
            <div><Label>ملاحظات</Label><Textarea rows={2} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} placeholder="مثل: بعد الظهر، اتصال قبل الوصول..." /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDlg(false)}>إلغاء</Button>
            <Button onClick={create} data-testid="save-delivery-btn">إنشاء</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
